import { Form, Input } from 'antd';
import Box from 'components/Box';
import Button from 'components/Button';
import Modal from 'components/Modal';
import Text from 'components/Text';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListApis from 'features/ticketList/services/apis';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { getMessageError, IResponseDataBody } from 'services/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import toast from 'utils/toast';
import TablePartialVoid from './TablePartialVoid';

interface IPartialVoidFormValues {
  billId: string;
  items?: {
    id: string;
    voidAmount: number;
    staffName: string;
    amount: number;
    serviceName: string;
    avatar: string;
  }[];
}

type Ref = {
  open: (amountVoid: number) => void;
};

export interface PartialVoidAllocationModalRef {
  open: (amountVoid: number) => void;
}

const PartialVoidAllocationModal = forwardRef<Ref, {}>((_, ref) => {
  const [visible, setVisible] = useState(false);
  const [amountVoid, setAmountVoid] = useState(0);
  const ticketDetails = ticketListSelectors.ticketDetailsData.data();
  const [form] = Form.useForm<IPartialVoidFormValues>();
  const items = Form.useWatch('items', form);
  const dispatch = useAppDispatch();

  const handleClose = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (value: IPartialVoidFormValues) => {
    try {
      const body = value.items?.map((item) => ({
        id: item.id,
        voidAmount: item.voidAmount,
      }));

      const res: IResponseDataBody = await ticketListApis.updateSaleVoidTicket(body || []);
      if (res?.data?.data) {
        toast.success('Partial void ticket successfully');
        dispatch(ticketListActions.getTicketDetails.fetch(value.billId));
        handleClose();
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to partial void ticket');
    }
  };

  const isEqual = useMemo(() => {
    const totalVoid = items?.reduce((sum: number, item: any) => sum + Number(item.voidAmount || 0), 0) || 0;

    return totalVoid === amountVoid;
  }, [items, amountVoid]);

  useImperativeHandle(ref, () => ({
    open: (amountVoid: number) => {
      setAmountVoid(amountVoid);
      setVisible(true);

      if (!ticketDetails) return;

      const rows = ticketDetails.items.flatMap((staff) =>
        staff.services.map((service) => ({
          id: service.id,
          staffName: staff.staffName,
          avatar: staff.avatar || '',
          serviceName: service.itemName,
          amount: service.price,
          voidAmount: 0,
        })),
      );

      form.setFieldsValue({
        billId: ticketDetails.billId,
        items: rows,
      });
    },
  }));

  const handleClear = () => {
    form.setFieldsValue({
      items: items?.map((item) => ({
        ...item,
        voidAmount: 0,
      })),
    });
  };

  return (
    <StyledModal
      visible={visible}
      modalTitle='ALLOCATE AMOUNT'
      noneFooterStyle
      containerPadding={1.5}
      onClose={handleClose}
      onSubmit={() => form.submit()}
      v2
      noneBodyStyle
      width={'1000px'}
      closable={false}
      footer={
        <FooterAction>
          <Button ntype='SECONDARY' htmlType='button' onClick={handleClose} cardCashier width='100%'>
            Close
          </Button>
          <Button ntype='TERTIARY' htmlType='button' width='100%' onClick={handleClear}>
            Clear
          </Button>
          <Button
            ntype='PRIMARY'
            htmlType='button'
            cardCashier
            width='100%'
            disabled={!isEqual}
            onClick={() => form.submit()}
          >
            OK
          </Button>
        </FooterAction>
      }
      destroyOnClose
    >
      <Wrapper className='v2' form={form} validateTrigger={['onSubmit']} onFinish={handleSubmit} layout='vertical'>
        <OriginalPrice>
          <Box display='flex' alignItems='center' gap='2'>
            <Text variant='H7' color='v2_primary_bg_6' style={{ fontWeight: 500 }}>
              Amount Void:
            </Text>
          </Box>
          <Text variant='H3' color='v2_primary_bg_6'>
            ${amountVoid?.toFixed(2) || '0.00'}
          </Text>
        </OriginalPrice>
        <Form.Item name='items' hidden>
          <Input />
        </Form.Item>
        <Form.Item name='billId' hidden>
          <Input />
        </Form.Item>
        <TablePartialVoid items={items || []} />
        {!isEqual && (
          <Text variant='H8' color='v2_other_3' style={{ fontWeight: 500, textAlign: 'center' }}>
            The total void amount does not match the allocated amount.
          </Text>
        )}
      </Wrapper>
    </StyledModal>
  );
});

PartialVoidAllocationModal.displayName = 'PartialVoidAllocationModal';
export default PartialVoidAllocationModal;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    height: 100%;
    overflow-y: hidden !important;
  }
`;

const Wrapper = styled(Form<IPartialVoidFormValues>)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Poppins !important;

  .ant-form-item {
    margin-bottom: 0;
  }
`;

const OriginalPrice = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const FooterAction = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 16px;

  .CONTENT_2 {
    font-family: Poppins;
    font-size: 20px;
  }
`;
